import React from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaLock, FaEye, FaUserCheck, FaCookieBite } from 'react-icons/fa';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      {/* Hero Section */}
      <section className="policy-hero">
        <div className="container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Privacy Policy</h1>
            <p>Your privacy and trust are paramount to us. Learn how we protect and manage your personal information.</p>
            <div className="last-updated">
              <FaShieldAlt />
              <span>Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="policy-content">
        <div className="container">
          <div className="content-wrapper">
            
            {/* Introduction */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Introduction</h2>
              <p>Vastré By Vijh ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, make purchases, or interact with our services.</p>
              <p>By using our website and services, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please do not use our website.</p>
            </motion.div>

            {/* Information We Collect */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaEye />
                Information We Collect
              </h2>
              
              <h3>Personal Information</h3>
              <p>We collect personal information that you voluntarily provide to us, including:</p>
              <ul>
                <li><strong>Contact Information:</strong> Name, email address, phone number, and mailing address</li>
                <li><strong>Account Information:</strong> Username, password, and account preferences</li>
                <li><strong>Purchase Information:</strong> Order history, payment details, and shipping information</li>
                <li><strong>Communication Data:</strong> Messages, feedback, and customer service interactions</li>
              </ul>

              <h3>Automatically Collected Information</h3>
              <p>When you visit our website, we automatically collect certain information:</p>
              <ul>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent on pages, and navigation patterns</li>
                <li><strong>Technical Data:</strong> Error logs, performance data, and security information</li>
              </ul>
            </motion.div>

            {/* How We Use Your Information */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaUserCheck />
                How We Use Your Information
              </h2>
              <p>We use the information we collect for various purposes, including:</p>
              <ul>
                <li><strong>Service Provision:</strong> Processing orders, managing accounts, and providing customer support</li>
                <li><strong>Communication:</strong> Sending order confirmations, updates, and marketing communications (with your consent)</li>
                <li><strong>Website Improvement:</strong> Analyzing usage patterns and enhancing user experience</li>
                <li><strong>Security:</strong> Protecting against fraud, unauthorized access, and other security threats</li>
                <li><strong>Legal Compliance:</strong> Meeting legal obligations and enforcing our terms of service</li>
              </ul>
            </motion.div>

            {/* Cookies and Tracking Technologies */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaCookieBite />
                Cookies and Tracking Technologies
              </h2>
              <p>We use cookies and similar tracking technologies to enhance your browsing experience:</p>
              
              <h3>Essential Cookies</h3>
              <p>These cookies are necessary for the website to function properly and cannot be disabled:</p>
              <ul>
                <li>Authentication and security cookies</li>
                <li>Shopping cart and checkout functionality</li>
                <li>Basic website navigation</li>
              </ul>

              <h3>Analytics Cookies</h3>
              <p>These cookies help us understand how visitors interact with our website:</p>
              <ul>
                <li>Page views and user behavior analysis</li>
                <li>Website performance monitoring</li>
                <li>Traffic source identification</li>
              </ul>

              <h3>Marketing Cookies</h3>
              <p>These cookies are used for personalized advertising and marketing:</p>
              <ul>
                <li>Retargeting and remarketing campaigns</li>
                <li>Social media integration</li>
                <li>Personalized content recommendations</li>
              </ul>

              <p>You can control cookie preferences through your browser settings. However, disabling certain cookies may affect website functionality.</p>
            </motion.div>

            {/* Information Sharing */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaLock />
                Information Sharing and Disclosure
              </h2>
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
              
              <h3>Service Providers</h3>
              <p>We work with trusted third-party service providers who assist us in:</p>
              <ul>
                <li>Payment processing and financial transactions</li>
                <li>Order fulfillment and shipping</li>
                <li>Website hosting and technical support</li>
                <li>Customer service and communication</li>
              </ul>

              <h3>Legal Requirements</h3>
              <p>We may disclose your information when required by law or to:</p>
              <ul>
                <li>Comply with legal processes and court orders</li>
                <li>Protect our rights, property, and safety</li>
                <li>Investigate potential violations of our terms</li>
                <li>Prevent fraud and security threats</li>
              </ul>

              <h3>Business Transfers</h3>
              <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction.</p>
            </motion.div>

            {/* Data Security */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h2>
                <FaShieldAlt />
                Data Security and Protection
              </h2>
              <p>We implement comprehensive security measures to protect your personal information:</p>
              <ul>
                <li><strong>Encryption:</strong> All sensitive data is encrypted using industry-standard protocols</li>
                <li><strong>Access Controls:</strong> Strict access controls limit who can access your information</li>
                <li><strong>Regular Audits:</strong> We conduct regular security assessments and updates</li>
                <li><strong>Employee Training:</strong> Our team is trained on data protection best practices</li>
                <li><strong>Incident Response:</strong> We have procedures in place for security incidents</li>
              </ul>
              
              <p>While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but commit to using commercially acceptable means to protect your personal information.</p>
            </motion.div>

            {/* Your Rights */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Your Rights and Choices</h2>
              <p>You have certain rights regarding your personal information:</p>
              
              <h3>Access and Control</h3>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              </ul>

              <h3>Communication Preferences</h3>
              <ul>
                <li><strong>Marketing Communications:</strong> Opt-out of marketing emails and notifications</li>
                <li><strong>Cookie Preferences:</strong> Manage cookie settings through your browser</li>
                <li><strong>Account Settings:</strong> Update preferences in your account dashboard</li>
              </ul>

              <p>To exercise these rights, please contact us using the information provided below.</p>
            </motion.div>

            {/* Data Retention */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              viewport={{ once: true }}
            >
              <h2>Data Retention</h2>
              <p>We retain your personal information for as long as necessary to:</p>
              <ul>
                <li>Provide our services and maintain your account</li>
                <li>Comply with legal obligations and tax requirements</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Improve our services and user experience</li>
              </ul>
              
              <p>When we no longer need your information, we will securely delete or anonymize it in accordance with our data retention policies.</p>
            </motion.div>

            {/* International Transfers */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>International Data Transfers</h2>
              <p>Your personal information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.</p>
            </motion.div>

            {/* Children's Privacy */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              viewport={{ once: true }}
            >
              <h2>Children's Privacy</h2>
              <p>Our website is not intended for children under the age of 16. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.</p>
            </motion.div>

            {/* Changes to Policy */}
            <motion.div
              className="policy-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              viewport={{ once: true }}
            >
              <h2>Changes to This Privacy Policy</h2>
              <p>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by:</p>
              <ul>
                <li>Posting the updated policy on our website</li>
                <li>Sending email notifications to registered users</li>
                <li>Displaying prominent notices on our website</li>
              </ul>
              
              <p>Your continued use of our website after any changes constitutes acceptance of the updated policy.</p>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              className="policy-section contact-section"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.1 }}
              viewport={{ once: true }}
            >
              <h2>Contact Us</h2>
              <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
              
              <div className="contact-info">
                <div className="contact-item">
                  <strong>Email:</strong> info@vastrebvijh.com
                </div>
                <div className="contact-item">
                  <strong>Address:</strong> PROPERTY NO. 55 THIRD FLOOR, SAIDULAJAB NEW DELHI 110030, NEAR WESTEND MARGIN LANE-2
                </div>
              </div>
              
              <p>We will respond to your inquiry within 30 days and work to resolve any concerns you may have about our privacy practices.</p>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
